/**
 * Where to send a browser once it has signed in, if it arrived asking for
 * that: `/v1/oauth/authorize` sends a signed-out human here with `?next=`
 * before returning them to the consent screen it could not show yet.
 *
 * Read once, the same idiom as `consumePasswordToken` and `consumeAuthError`:
 * captured into state on first render, then taken out of the address bar so
 * it does not linger in history or a bookmark.
 */
export function consumeReturnPath(): string | null {
  if (typeof window === 'undefined') return null
  const found = new URLSearchParams(window.location.search).get('next')

  const url = new URL(window.location.href)
  if (url.searchParams.has('next')) {
    url.searchParams.delete('next')
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  }

  return found ? sameOriginReturnPath(found) : null
}

/**
 * Same-origin and nowhere else, or `null`.
 *
 * A path stagepost itself hands out is always this shape, but the query
 * string is a URL anyone can type, so this is the one place standing between
 * a crafted `?next=` and an open redirect. Resolving against the page's own
 * origin, rather than pattern-matching the string, is the point: a browser
 * strips tab, CR and LF out of a URL before it navigates, so `/%09/evil.test`
 * *is* `//evil.test` by the time it matters, and a regex that looks at the
 * string before that stripping happens misses it. `new URL` does the same
 * stripping, so resolving first and checking `origin` after catches it too,
 * and the leading control-character refusal below exists only to say so
 * out loud rather than lean on that as the whole defence. What is handed
 * back is the resolved path, not the original string, so a form nothing
 * above rejects but the resolver still normalised cannot sneak through
 * unchanged either.
 */
function sameOriginReturnPath(value: string): string | null {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: the point of this check is to find them.
  if (/[\x00-\x20\x7f]/.test(value)) return null
  let target: URL
  try {
    target = new URL(value, window.location.origin)
  } catch {
    return null
  }
  if (target.origin !== window.location.origin) return null
  return `${target.pathname}${target.search}${target.hash}`
}
